/**
 * Notifications are split into two inboxes in the top bar:
 *  - `conversation`: chat / message notifications (asesor ↔ abogado/bancarizador, etc.)
 *    shown under the dedicated "conversations" (cloud) icon.
 *  - `general`: business event notifications (closings, properties, legal status…)
 *    shown under the notifications (bell) icon.
 */
export type NotificationCategory = 'conversation' | 'general';

export interface Notification {
    id: string;
    icon?: string;
    image?: string;
    title?: string;
    description?: string;
    time: string;
    link?: string;
    useRouter?: boolean;
    read: boolean;
    category: NotificationCategory;
    templateCode?: string;
}

export interface DbNotification {
    id: string;
    request_id?: string;
    recipient_type: string;
    recipient_user_id: string;
    recipient_email?: string;
    template_code: string;
    title: string;
    message: string;
    description?: string;
    channel: string;
    status: string;
    sent_at?: string;
    delivered_at?: string;
    read_at?: string;
    error_message?: string;
    icon?: string;
    image?: string;
    link?: string;
    use_router?: boolean;
    source?: string;
    metadata?: Record<string, any>;
    created_at: string;
}

/**
 * Template codes whose name matches this pattern are treated as
 * conversation/message notifications (e.g. `legal_message_approved`,
 * `legal_message_pending`). Extend the pattern as new chat-based flows
 * are added (bancarización / trámite responses, etc.).
 */
const CONVERSATION_TEMPLATE_PATTERN = /message|chat|conversation/i;

export function getNotificationCategory(db: DbNotification): NotificationCategory {
    return CONVERSATION_TEMPLATE_PATTERN.test(db.template_code ?? '')
        ? 'conversation'
        : 'general';
}

export function mapDbToNotification(db: DbNotification): Notification {
    return {
        id: db.id,
        icon: db.icon || undefined,
        image: db.image || undefined,
        title: db.title || undefined,
        description: db.description || db.message || undefined,
        time: db.created_at,
        link: db.link || undefined,
        useRouter: db.use_router ?? false,
        read: db.read_at != null,
        category: getNotificationCategory(db),
        templateCode: db.template_code || undefined,
    };
}
